import React from "react";
import {
  Badge,
  IconButton,
  Menu,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Typography,
  Divider,
  Button,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { useAppDispatch, useAppSelector } from "../hooks/hooks";
import {
  markAllAsRead,
  clearNotifications,
  markAsRead,
} from "../redux/notificationSlice";

const NotificationCenter: React.FC = () => {
  const dispatch = useAppDispatch();
  const { notifications, unreadCount } = useAppSelector(
    (state) => state.notifications
  );
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMarkAllAsRead = () => {
    dispatch(markAllAsRead());
  };

  const handleClearAll = () => {
    dispatch(clearNotifications());
    handleMenuClose();
  };

  const handleNotificationClick = (id: string) => {
    dispatch(markAsRead(id));
    // Add navigation logic here based on notification data if needed
  };

  return (
    <>
      <IconButton
        color="inherit"
        aria-label="notifications"
        onClick={handleMenuOpen}
      >
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right", // This positions the anchor point
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right", // This positions where the menu grows from
        }}
        slotProps={{
          paper: {
            elevation: 1,
            sx: {
              width: 350,
              maxHeight: "80vh",
              overflowY: "auto",
            },
          },
        }}
      >
        <MenuItem disabled>
          <Typography variant="subtitle1" fontWeight="bold">
            Notifications
          </Typography>
        </MenuItem>
        <Divider />

        {notifications.length === 0 ? (
          <MenuItem>
            <Typography variant="body2">No notifications</Typography>
          </MenuItem>
        ) : (
          <>
            <List dense>
              {notifications.map((notification) => (
                <ListItem
                  key={notification.id}
                  component="button"
                  onClick={() => handleNotificationClick(notification.id)}
                  sx={{
                    bgcolor: notification.read ? undefined : "action.hover",
                  }}
                >
                  <ListItemText
                    primary={notification.title}
                    secondary={notification.message}
                    secondaryTypographyProps={{
                      noWrap: true,
                    }}
                  />
                </ListItem>
              ))}
            </List>
            <Divider />
            <MenuItem>
              <Button
                size="small"
                onClick={handleMarkAllAsRead}
                disabled={unreadCount === 0}
              >
                Mark all as read
              </Button>
              <Button
                size="small"
                onClick={handleClearAll}
                disabled={notifications.length === 0}
                sx={{ ml: 1 }}
              >
                Clear all
              </Button>
            </MenuItem>
          </>
        )}
      </Menu>
    </>
  );
};

export default NotificationCenter;
